/**
 * Auth API Client (Phase 11)
 * ===========================
 * JWT authentication API client for frontend
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api'

export interface LoginRequest {
  username: string
  password: string
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export interface RefreshRequest {
  refresh_token: string
}

export interface RefreshResponse {
  access_token: string
  token_type: string
  expires_in: number
}

export interface UserInfo {
  username: string
  role: string
  sub: string
}

/**
 * Login with username and password
 */
export async function login(username: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Login failed' }))
    throw new Error(error.detail || 'Login failed')
  }

  return response.json()
}

/**
 * Refresh access token using refresh token
 */
export async function refreshToken(refreshToken: string): Promise<RefreshResponse> {
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Token refresh failed' }))
    throw new Error(error.detail || 'Token refresh failed')
  }

  return response.json()
}

/**
 * Logout (revoke tokens)
 */
export async function logout(accessToken: string): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    // Silently fail (client-side logout always succeeds)
    console.warn('Logout request failed, continuing with client-side cleanup')
  }
}

/**
 * Get current user info
 */
export async function getCurrentUser(accessToken: string): Promise<UserInfo> {
  const response = await fetch(`${API_BASE}/auth/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get user info' }))
    throw new Error(error.detail || 'Failed to get user info')
  }

  return response.json()
}

/**
 * Token storage helpers
 */
export const TOKEN_STORAGE_KEY = 'auth_tokens'

export interface StoredTokens {
  access_token: string
  refresh_token: string
  expires_at: number // Unix timestamp
}

function getStorage(): globalThis.Storage | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage
  } catch {
    return null
  }
}

/**
 * Save tokens to localStorage
 */
export function saveTokens(tokens: LoginResponse): void {
  const storage = getStorage()
  if (!storage) return

  const expiresAt = Date.now() + tokens.expires_in * 1000
  const storedTokens: StoredTokens = {
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    expires_at: expiresAt,
  }
  storage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(storedTokens))
}

/**
 * Get tokens from localStorage
 */
export function getTokens(): StoredTokens | null {
  const storage = getStorage()
  if (!storage) return null

  const stored = storage.getItem(TOKEN_STORAGE_KEY)
  if (!stored) return null

  try {
    return JSON.parse(stored)
  } catch {
    return null
  }
}

/**
 * Clear tokens from localStorage
 */
export function clearTokens(): void {
  const storage = getStorage()
  if (!storage) return

  storage.removeItem(TOKEN_STORAGE_KEY)
}

/**
 * Check if access token is expired
 */
export function isTokenExpired(tokens: StoredTokens): boolean {
  return Date.now() >= tokens.expires_at
}

/**
 * Auto-refresh token if expired
 */
export async function autoRefreshToken(): Promise<string | null> {
  const tokens = getTokens()
  if (!tokens) return null

  // If not expired, return current token
  if (!isTokenExpired(tokens)) {
    return tokens.access_token
  }

  // Token expired, try to refresh
  try {
    const refreshResponse = await refreshToken(tokens.refresh_token)

    // Update stored tokens
    const storage = getStorage()
    if (!storage) {
      return refreshResponse.access_token
    }

    const expiresAt = Date.now() + refreshResponse.expires_in * 1000
    const updatedTokens: StoredTokens = {
      access_token: refreshResponse.access_token,
      refresh_token: tokens.refresh_token, // Keep same refresh token
      expires_at: expiresAt,
    }
    storage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(updatedTokens))

    return refreshResponse.access_token
  } catch {
    // Refresh failed, clear tokens
    clearTokens()
    return null
  }
}
